/**
 * Utility functions for parsing and expanding plot instructions
 * Supports band patterns and fixed wavelength values for astronomical data
 */

/**
 * Parse JSON plot instructions from string
 * Supports both single plot (defaultPlot) and multiple plots (plots array)
 * @param {string} instructionsString - JSON string from database
 * @returns {object|null} Parsed instructions or null if invalid
 */
export const parsePlotInstructions = (instructionsString) => {
  if (!instructionsString || typeof instructionsString !== 'string') {
    return null;
  }

  try {
    const instructions = JSON.parse(instructionsString);

    // Support both formats:
    // 1. New format: { plots: [...] }
    // 2. Legacy format: { defaultPlot: {...} }
    if (instructions.plots && Array.isArray(instructions.plots)) {
      return instructions;
    } else if (instructions.defaultPlot) {
      // Convert legacy format to new format
      return { plots: [instructions.defaultPlot] };
    }

    return null;
  } catch (error) {
    console.warn('Failed to parse plot instructions:', error);
    return null;
  }
};

/**
 * Expand band pattern into actual column names
 * Example: "column_{band}" with bands ["u", "g", "r"] => ["column_u", "column_g", "column_r"]
 *
 * @param {string} pattern - Pattern string with {band} placeholder
 * @param {string[]} bands - Array of band identifiers
 * @returns {string[]} Expanded column names
 */
export const expandBandPattern = (pattern, bands) => {
  if (!pattern || !bands || !Array.isArray(bands)) {
    return [];
  }

  return bands.map(band => pattern.replace('{band}', band));
};

/**
 * Parse array from string representation
 * @param {string|array} value - String like "[1,2,3]" or already an array
 * @returns {number[]|null} Parsed numeric array or null if invalid
 */
export const parseArrayColumn = (value) => {
  if (Array.isArray(value)) {
    return value.map(v => Number(v)).filter(v => !isNaN(v));
  }

  if (typeof value === 'string') {
    try {
      // Try to parse as JSON array
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(v => Number(v)).filter(v => !isNaN(v));
      }
    } catch (e) {
      // Not valid JSON, try manual parsing
      const match = value.match(/^\[(.*)\]$/);
      if (match) {
        const numbers = match[1].split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
        if (numbers.length > 0) return numbers;
      }
    }
  }

  return null;
};

/**
 * Extract numeric values from object data for specified columns
 * @param {object} data - Object with column data
 * @param {string[]} columns - Column names to extract
 * @returns {number[]} Array of numeric values (null for missing/non-numeric)
 */
export const extractColumnValues = (data, columns) => {
  if (!data || !columns || !Array.isArray(columns)) {
    return [];
  }

  return columns.map(col => {
    const value = data[col];
    return (value !== null && value !== undefined && !isNaN(value)) ? Number(value) : null;
  });
};

/**
 * Extract axis values from axis configuration and data object (used for multi-series and colorColumn)
 * @param {object} axisConfig - Axis configuration
 * @param {object} data - Data object
 * @returns {Array} Array of values
 */
const extractAxisData = (axisConfig, data) => {
  if (!axisConfig || !data) return [];
  if (axisConfig.type === 'fixed') {
    return axisConfig.values || [];
  } else if (axisConfig.type === 'column') {
    const value = data[axisConfig.column];
    if (value !== null && value !== undefined) {
      return Array.isArray(value) ? value.map(Number) : [Number(value)];
    }
  } else if (axisConfig.type === 'array') {
    return parseArrayColumn(data[axisConfig.column]) || [];
  } else if (axisConfig.type === 'pattern') {
    const columns = expandBandPattern(axisConfig.pattern, axisConfig.bands);
    return extractColumnValues(data, columns);
  }
  return [];
};

/**
 * Build valid {x, y} point pairs from two value arrays, filtering nulls/NaN
 * @param {Array} xValues
 * @param {Array} yValues
 * @returns {{x: number, y: number}[]}
 */
const buildValidPoints = (xValues, yValues) => {
  const minLen = Math.min(xValues.length, yValues.length);
  const points = [];
  for (let i = 0; i < minLen; i++) {
    const x = Number(xValues[i]);
    const y = Number(yValues[i]);
    if (xValues[i] !== null && yValues[i] !== null && !isNaN(x) && !isNaN(y)) {
      points.push({ x, y });
    }
  }
  return points;
};

/**
 * Build default plot configuration from instructions
 * @param {object} plotInstructions - Parsed plot instructions
 * @param {object} data - Object data
 * @returns {object|null} Plot configuration ready for Chart.js or null if cannot build
 */
export const buildDefaultPlot = (plotInstructions, data) => {
  if (!plotInstructions || !data) {
    return null;
  }

  const { defaultPlot } = plotInstructions;
  if (!defaultPlot) {
    return null;
  }

  const { type, title, xAxis, yAxis, series, options = {} } = defaultPlot;

  // === Multi-series: plot has a 'series' array — overlay multiple series in one chart ===
  if (series && Array.isArray(series) && series.length > 0) {
    const seriesData = [];
    let xLabel = defaultPlot.xLabel || 'X';
    let yLabel = defaultPlot.yLabel || 'Y';

    series.forEach((s, i) => {
      const xVals = extractAxisData(s.xAxis, data);
      const yVals = extractAxisData(s.yAxis, data);

      if (i === 0) {
        xLabel = s.xAxis?.label || defaultPlot.xLabel || 'X';
        yLabel = s.yAxis?.label || defaultPlot.yLabel || 'Y';
      }

      const points = buildValidPoints(xVals, yVals);
      if (points.length > 0) {
        seriesData.push({ label: s.label || `Series ${i + 1}`, data: points, color: s.color || undefined });
      }
    });

    if (seriesData.length === 0) {
      console.warn('No valid series data for multi-series plot');
      return null;
    }

    return {
      type: type || 'scatter',
      title: title || '',
      xLabel,
      yLabel,
      isMultiSeries: true,
      seriesData,
      options: { connectPoints: false, showLegend: true, invertYAxis: false, ...options },
    };
  }

  // Process X-axis
  let xValues = [];
  let xLabel = xAxis?.label || 'X';

  console.log('🔍 [buildDefaultPlot] X-axis processing:');
  console.log('  - X-axis type:', xAxis?.type);

  if (xAxis?.type === 'fixed') {
    // Use fixed values (e.g., wavelengths)
    xValues = xAxis.values || [];
    console.log('  - Using fixed values:', xValues);
  } else if (xAxis?.type === 'column') {
    // Use column from data
    const value = data[xAxis.column];
    if (value !== null && value !== undefined) {
      xValues = Array.isArray(value) ? value : [value];
    }
  } else if (xAxis?.type === 'array') {
    // Parse array column (handles string arrays like "[1,2,3]")
    const arrayData = parseArrayColumn(data[xAxis.column]);
    if (arrayData && arrayData.length > 0) {
      xValues = arrayData;
      console.log('  - Parsed array column:', xAxis.column, '→', arrayData.length, 'values');
    } else {
      console.log('  - Failed to parse array column:', xAxis.column);
    }
  } else if (xAxis?.type === 'pattern') {
    // Expand pattern and get values
    const columns = expandBandPattern(xAxis.pattern, xAxis.bands);
    xValues = extractColumnValues(data, columns);
  }

  // Process Y-axis
  let yValues = [];
  let yLabel = yAxis?.label || 'Y';
  let columnLabels = [];

  if (yAxis?.type === 'pattern') {
    // Expand pattern to get column names
    const columns = expandBandPattern(yAxis.pattern, yAxis.bands);
    console.log('🔍 [buildDefaultPlot] Y-axis pattern expansion:');
    console.log('  - Pattern:', yAxis.pattern);
    console.log('  - Bands:', yAxis.bands);
    console.log('  - Expanded columns:', columns);
    console.log('  - Available columns in data:', Object.keys(data));
    console.log('  - Checking which columns exist:');
    columns.forEach(col => {
      console.log(`    - ${col}: ${col in data ? 'EXISTS' : 'MISSING'} (value: ${data[col]})`);
    });
    yValues = extractColumnValues(data, columns);
    console.log('  - Extracted values:', yValues);
    columnLabels = yAxis.bands || columns;
  } else if (yAxis?.type === 'column') {
    // Single column
    const value = data[yAxis.column];
    if (value !== null && value !== undefined) {
      yValues = Array.isArray(value) ? value : [value];
      columnLabels = [yAxis.column];
    }
  } else if (yAxis?.type === 'array') {
    // Parse array column (handles string arrays like "[1,2,3]")
    const arrayData = parseArrayColumn(data[yAxis.column]);
    if (arrayData && arrayData.length > 0) {
      yValues = arrayData;
      columnLabels = arrayData.map((_, i) => `Point ${i + 1}`);
      console.log('  - Parsed Y array column:', yAxis.column, '→', arrayData.length, 'values');
    } else {
      console.log('  - Failed to parse Y array column:', yAxis.column);
    }
  } else if (yAxis?.type === 'fixed') {
    // Fixed values
    yValues = yAxis.values || [];
    columnLabels = yAxis.labels || yValues.map((_, i) => `Point ${i + 1}`);
  }

  // === colorColumn: group scatter points by unique values of a third column ===
  if (options.colorColumn && xValues.length > 0 && yValues.length > 0) {
    let colorVals = parseArrayColumn(data[options.colorColumn]);
    // Handle scalar colorColumn (single value, not an array): expand to fill all data points
    if (!colorVals) {
      const scalar = data[options.colorColumn];
      if (scalar !== null && scalar !== undefined && !isNaN(Number(scalar))) {
        const len = Math.min(xValues.length, yValues.length);
        colorVals = Array(len).fill(Number(scalar));
      }
    }
    if (colorVals && colorVals.length > 0) {
      const minLen = Math.min(xValues.length, yValues.length, colorVals.length);
      const groups = {};
      for (let i = 0; i < minLen; i++) {
        if (xValues[i] !== null && yValues[i] !== null) {
          const key = String(colorVals[i]);
          if (!groups[key]) groups[key] = [];
          groups[key].push({ x: Number(xValues[i]), y: Number(yValues[i]) });
        }
      }
      const colorLabelsMap = options.colorLabels || {};
      const seriesData = Object.entries(groups)
        .sort(([a], [b]) => (Number(a) - Number(b)) || a.localeCompare(b))
        .map(([key, pts]) => ({ label: colorLabelsMap[key] || key, data: pts }));

      if (seriesData.length > 0) {
        return {
          type: type || 'scatter',
          title: title || '',
          xLabel,
          yLabel,
          isMultiSeries: true,
          seriesData,
          options: { connectPoints: false, showLegend: true, invertYAxis: false, ...options },
        };
      }
    }
  }

  // Ensure x and y have same length
  const minLength = Math.min(xValues.length, yValues.length);
  if (minLength === 0) {
    console.warn('No valid data points for default plot');
    return null;
  }

  // Filter out null values (keep paired x,y together)
  const validPoints = [];
  const validLabels = [];
  for (let i = 0; i < minLength; i++) {
    if (xValues[i] !== null && yValues[i] !== null) {
      validPoints.push({ x: xValues[i], y: yValues[i] });
      if (columnLabels[i]) {
        validLabels.push(columnLabels[i]);
      }
    }
  }

  if (validPoints.length === 0) {
    console.warn('No valid (non-null) data points for default plot');
    return null;
  }

  // Build Chart.js configuration
  const config = {
    type: type || 'scatter',
    title: title || 'Default Plot',
    xLabel,
    yLabel,
    data: validPoints,
    labels: validLabels,
    options: {
      connectPoints: options.connectPoints || false,
      showLegend: options.showLegend !== false,
      invertYAxis: options.invertYAxis || false,
      ...options
    }
  };

  return config;
};

/**
 * Validate a single plot configuration
 * @param {object} plot - Single plot configuration
 * @param {number} index - Index in plots array (for error messages)
 * @returns {string[]} Array of error messages
 */
const validateSinglePlot = (plot, index = 0) => {
  const errors = [];
  const prefix = `Plot ${index + 1}:`;

  // Validate plot type
  const validTypes = ['scatter', 'line', 'bar'];
  if (plot.type && !validTypes.includes(plot.type)) {
    errors.push(`${prefix} Invalid plot type: ${plot.type}. Must be one of: ${validTypes.join(', ')}`);
  }

  // Validate axes — either 'series' array OR xAxis+yAxis must be present
  if (plot.series) {
    if (!Array.isArray(plot.series) || plot.series.length === 0) {
      errors.push(`${prefix} series must be a non-empty array`);
    }
  } else {
    if (!plot.xAxis) {
      errors.push(`${prefix} Missing xAxis configuration`);
    } else {
      const { type, values, pattern, bands, column } = plot.xAxis;
      if (!type) {
        errors.push(`${prefix} xAxis missing type field`);
      }
      if (type === 'fixed' && (!values || !Array.isArray(values))) {
        errors.push(`${prefix} xAxis with type "fixed" requires values array`);
      }
      if (type === 'pattern' && (!pattern || !bands)) {
        errors.push(`${prefix} xAxis with type "pattern" requires pattern and bands fields`);
      }
      if ((type === 'column' || type === 'array') && !column) {
        errors.push(`${prefix} xAxis with type "${type}" requires column field`);
      }
    }

    if (!plot.yAxis) {
      errors.push(`${prefix} Missing yAxis configuration`);
    } else {
      const { type, values, pattern, bands, column } = plot.yAxis;
      if (!type) {
        errors.push(`${prefix} yAxis missing type field`);
      }
      if (type === 'fixed' && (!values || !Array.isArray(values))) {
        errors.push(`${prefix} yAxis with type "fixed" requires values array`);
      }
      if (type === 'pattern' && (!pattern || !bands)) {
        errors.push(`${prefix} yAxis with type "pattern" requires pattern and bands fields`);
      }
      if ((type === 'column' || type === 'array') && !column) {
        errors.push(`${prefix} yAxis with type "${type}" requires column field`);
      }
    }
  }

  return errors;
};

/**
 * Validate plot instructions structure
 * @param {object} instructions - Parsed plot instructions
 * @returns {object} Validation result { valid: boolean, errors: string[] }
 */
export const validatePlotInstructions = (instructions) => {
  const errors = [];

  if (!instructions) {
    return { valid: false, errors: ['Instructions are null or undefined'] };
  }

  if (!instructions.plots || !Array.isArray(instructions.plots)) {
    return { valid: false, errors: ['Missing or invalid plots array'] };
  }

  if (instructions.plots.length === 0) {
    return { valid: false, errors: ['plots array is empty'] };
  }

  // Validate each plot
  instructions.plots.forEach((plot, index) => {
    const plotErrors = validateSinglePlot(plot, index);
    errors.push(...plotErrors);
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Example plot instructions for SPLUS 12-band photometry
 */
export const EXAMPLE_SPLUS_INSTRUCTIONS = {
  defaultPlot: {
    type: 'scatter',
    title: 'SPLUS Photometry vs Wavelength',
    xAxis: {
      type: 'fixed',
      label: 'Wavelength (Å)',
      values: [3485, 3785, 3950, 4100, 4300, 4803, 5308, 6250, 6600, 7660, 8610, 9110]
    },
    yAxis: {
      type: 'pattern',
      label: 'Magnitude',
      pattern: '{band}_auto',
      bands: ['u', 'F378', 'F395', 'F410', 'F430', 'g', 'F515', 'r', 'F660', 'i', 'F861', 'z']
    },
    options: {
      connectPoints: true,
      showLegend: true,
      invertYAxis: true  // Magnitudes increase downward
    }
  }
};

export default {
  parsePlotInstructions,
  expandBandPattern,
  extractColumnValues,
  buildDefaultPlot,
  validatePlotInstructions,
  EXAMPLE_SPLUS_INSTRUCTIONS
};
